import { Document, Page } from 'react-pdf'
import { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  pdfFileSelector,
  pagesSelector,
  visibleStyledElementsPreviewSelector,
  pageTableLayoutsSelector,
} from 'apps/studio/store/selectors/pdf'
import {
  getTableDisplayMetrics,
  getColumnHeaderRowData,
  getTableBackgroundCss,
  resolveTableSheets,
  entriesForTableSheets,
} from 'apps/studio/lib/utils/entities'
import { activeSheetSelector } from 'apps/builder/store/selectors'
import { styleDefaults } from 'apps/studio/lib/utils/styles'
import { backgroundStyleFromStyles } from 'apps/studio/lib/utils/color'
import { generateStyles } from 'apps/studio/components/pdfViewer/elements/PdfElement'
import { selectors, lib } from '@gocanvas/client'
import { isBlank } from 'apps/builder/lib/utils'
import {
  sheetsSelector,
  entriesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
const { clientEntriesSelector } = selectors
const { GC_BLANK_DEFAULT, BLANK, choiceTypes, dateTypes } = lib

const MultiLineValues = ({ value, label }) =>
  value.length
    ? value.map((value, i) => <p key={`multi-line-${i}`}>{value}</p>)
    : label

const TextValue = ({ value, label, type }) => {
  let displayValue = value

  if (choiceTypes.includes(type) && value === GC_BLANK_DEFAULT) {
    displayValue = ''
  }

  if (dateTypes.includes(type) && value === BLANK) {
    displayValue = ''
  }

  return (
    <div
      className={`element--label single-line--value ${
        displayValue ? '' : 'no-value'
      }`}
    >
      {displayValue || label}
    </div>
  )
}

const LongTextValue = ({ value, label }) => {
  const displayValue = isBlank(value) ? [] : String(value).split('\n')
  const hasValues = !!displayValue.length

  return (
    <div
      className={`element--label multi-line--value ${
        hasValues ? '' : 'no-value'
      }`}
    >
      <MultiLineValues value={displayValue} label={label} />
    </div>
  )
}

const MultiChoiceValue = ({ value, label }) => {
  const displayValue = value.filter(v => v !== 'GC_BLANK_DEFAULT')
  const hasValues = !!displayValue.length

  return (
    <div
      className={`element--label multi-line--value multi-choice--value ${
        hasValues ? '' : 'no-value'
      }`}
    >
      <MultiLineValues value={displayValue} label={label} />
    </div>
  )
}

const extractImageValue = value => {
  if (Array.isArray(value)) {
    return value[0]
  }
  return { url: value }
}

const ImageValue = ({ value, label }) => {
  const [aspectRatio, setAspectRatio] = useState('image--horizontal')

  const firstImage = extractImageValue(value)
  const { url, name = '' } = firstImage

  const setImageInfo = ({ target }) => {
    const { naturalWidth, naturalHeight } = target
    const aspectRatio =
      naturalWidth > naturalHeight ? 'image--horizontal' : 'image--vertical'
    setAspectRatio(aspectRatio)
  }

  return (
    <div className={`element--label ${value ? '' : 'no-value'}`}>
      {url ? (
        <img
          src={url}
          alt={name}
          className={`image--value  ${aspectRatio}`}
          onLoad={setImageInfo}
        />
      ) : (
        label
      )}
    </div>
  )
}

const CheckboxValue = ({ value, label, entry_guid }) => {
  return (
    <div className={`element--label`}>
      <div className="foundry--checkbox checkbox--value">
        <input
          className="checkbox__input"
          id={`checkbox-${entry_guid}`}
          name={`checkbox-${entry_guid}-${label}`}
          type="checkbox"
          readOnly
          checked={value == 'True'}
          value={`${label}`}
        />
        <label
          className="checkbox__label"
          htmlFor={`checkbox-${entry_guid}`}
        ></label>
      </div>
    </div>
  )
}

const StaticTextValue = ({ value, label }) => {
  // Value is present when static text is a dependent ref data field
  const valueToUse = isBlank(value) ? label : value
  const displayValue = isBlank(valueToUse) ? [] : String(valueToUse).split('\n')
  return (
    <div className="element--label multi-line--value">
      <MultiLineValues value={displayValue} label={label} />
    </div>
  )
}

const ValueComponents = {
  Text: TextValue,
  'Long Text': LongTextValue,
  Photo: ImageValue,
  Signature: ImageValue,
  Checkbox: CheckboxValue,
  'Single Choice': TextValue,
  'Multiple Choice': MultiChoiceValue,
  Dropdown: TextValue,
  'Static Text': StaticTextValue,
}

const PreviewElement = ({ element, entry }) => {
  const { position, styles, element_type } = element
  const elementStyles = generateStyles({ position, styles })
  const ValueComponent = ValueComponents[element_type] || TextValue

  return (
    <div className="page-element--wrapper" style={elementStyles}>
      <div className="page-element preview-element">
        <ValueComponent {...entry} />
      </div>
    </div>
  )
}

const PreviewTableCellValue = ({ element, entry }) => {
  const { styles = {}, element_type } = element
  const ValueComponent = ValueComponents[element_type] || TextValue
  const {
    fontColor,
    fontFamily,
    fontSize,
    horizontalAlignment,
    verticalAlignment,
    fontWeight,
    fontStyle,
    textDecoration,
  } = { ...styleDefaults, ...styles }

  return (
    <div
      className="static-table--cell-value"
      style={{
        color: fontColor,
        fontFamily,
        // Match .page-element .element--label so zoom/scale matches Edit + demo.
        fontSize: `calc(${fontSize}px * var(--scale-factor, 1))`,
        fontWeight,
        fontStyle,
        textDecoration,
        justifyContent:
          horizontalAlignment === 'left'
            ? 'flex-start'
            : horizontalAlignment === 'right'
              ? 'flex-end'
              : 'center',
        alignItems:
          verticalAlignment === 'top'
            ? 'flex-start'
            : verticalAlignment === 'bottom'
              ? 'flex-end'
              : 'center',
      }}
    >
      <ValueComponent {...entry} />
    </div>
  )
}

const PreviewTable = ({ tableLayout, tableElements }) => {
  const sheets = useSelector(sheetsSelector)
  const allEntries = useSelector(entriesSelector)
  const clientEntries = useSelector(clientEntriesSelector)
  const {
    showColumnHeader,
    hideTableTitle,
    titleHeight,
    columnHeaderHeight,
    displayHeight,
  } = getTableDisplayMetrics(tableLayout)
  const tableBackground = getTableBackgroundCss(tableLayout)

  const { keyFieldSheet, tableSheet } = resolveTableSheets(
    sheets,
    tableLayout.section_id
  )
  const tableEntries = entriesForTableSheets(
    allEntries,
    keyFieldSheet,
    tableSheet
  )

  const rowsByLayoutIdMap = clientEntries.reduce((acc, entry) => {
    if (entry.keyField && entry.keyfieldGuid) {
      const { layout_id: layoutId } =
        tableElements.find(el => el.entry_guid === entry.parentGuid) || {}

      if (!acc[layoutId]) acc[layoutId] = []

      const rowEntries = clientEntries.filter(
        e => e.rowGuid === entry.rowGuid && !e.keyField
      )
      acc[layoutId].push([entry, ...rowEntries])
    }

    return acc
  }, {})

  const colCount = tableLayout.columns?.length || 1

  const columnHeaderRow = showColumnHeader
    ? getColumnHeaderRowData(tableLayout, tableElements, tableEntries).map(
        ({ colIdx, position, label }) => (
          <div
            key={`col-header-preview-${colIdx}`}
            className="static-table--cell static-table--column-header"
            style={{
              '--cell-x': `${position.x}px`,
              '--cell-y': `${position.y}px`,
              '--cell-w': `${position.width}px`,
              '--cell-h': `${columnHeaderHeight}px`,
            }}
          >
            {label}
          </div>
        )
      )
    : null

  const previewElements = tableElements.map(element => {
    const { entry_guid, sort_order, layout_id, position } = element
    const rowNumber = Math.ceil(sort_order / colCount)

    const rows = rowsByLayoutIdMap[layout_id] || []
    const row = rows[rowNumber - 1] || []
    const entry = row.find(e => e.parentGuid === entry_guid)
    const showValue = entry && entry.pdf_visibility !== 1

    // Always paint the cell chrome here so borders stay continuous whether
    // the cell is empty or filled (page-element wrappers round down and leave gaps).
    return (
      <div
        key={`cell-${tableLayout.id}-${sort_order}`}
        className="static-table--cell"
        style={{
          '--cell-x': `${position.x}px`,
          '--cell-y': `${position.y}px`,
          '--cell-w': `${position.width}px`,
          '--cell-h': `${position.height}px`,
          ...backgroundStyleFromStyles(element.styles),
        }}
      >
        {showValue && (
          <PreviewTableCellValue element={element} entry={entry} />
        )}
      </div>
    )
  })

  const tablePosition = tableLayout.position || {}
  const parentSheet = sheets.find(
    sheet => sheet.section_id === tableLayout.section_id
  )

  const tableStyles = {
    '--st-x': `${tablePosition.x}px`,
    '--st-y': `${tablePosition.y}px`,
    '--st-w': `${tablePosition.width}px`,
    '--st-h': `${displayHeight || tablePosition.height || 0}px`,
    '--st-title-h': `${titleHeight}px`,
    ...(tableBackground ? { '--st-bg': tableBackground } : {}),
  }

  return (
    <div
      id={`table-layout-${tableLayout.id}`}
      className="static-table--frame"
      style={tableStyles}
    >
      {!hideTableTitle && (
        <div
          id={`table-${tableLayout.id}-title`}
          className="static-table--title static-table-preview--title"
        >
        {parentSheet?.description || parentSheet?.placeholder || 'Table'}
        </div>
      )}
      {columnHeaderRow}
      {previewElements}
    </div>
  )
}

const PreviewPage = ({ page, previewContainerSize }) => {
  const [pageDimensions, setPageDimensions] = useState({})
  const clientEntries = useSelector(clientEntriesSelector)
  const pageTableLayouts = useSelector(pageTableLayoutsSelector(page?.id))
  const { normalScreenElements, tableScreenElements } = useSelector(
    visibleStyledElementsPreviewSelector
  )

  const onLoadSuccess = page => {
    if (pageDimensions.width || pageDimensions.height) return
    const { width, height } = page
    const scaleX = previewContainerSize.width / width
    const scaleY = previewContainerSize.height / height
    const paddedHeight = previewContainerSize.height * 0.95
    const paddedWidth = previewContainerSize.width * 0.95
    const scaledWidth = width * scaleX
    const scaledHeight = height * scaleY

    const isLandscape = scaledWidth > scaledHeight
    const containerIsLandscape =
      previewContainerSize.width > previewContainerSize.height
    scaledWidth > paddedWidth && scaledHeight > paddedHeight
    const tooWide = scaledWidth > paddedWidth
    const tooTall = scaledHeight > paddedHeight

    if (containerIsLandscape && isLandscape) {
      const update = tooTall ? { height: paddedHeight } : { width: paddedWidth }
      setPageDimensions(update)
    } else if (containerIsLandscape && !isLandscape) {
      const update = tooWide ? { width: paddedWidth } : { height: paddedHeight }
      setPageDimensions(update)
    } else {
      setPageDimensions({
        width: paddedWidth,
      })
    }
  }

  return (
    <Page
      className="pdf-page foundry--surface"
      pageNumber={page?.page_number || 1}
      renderAnnotationLayer={false}
      renderTextLayer={false}
      onLoadSuccess={onLoadSuccess}
      {...pageDimensions}
    >
      {normalScreenElements.map(element => {
        const entry = clientEntries.find(
          entry => entry.guid === element.entry_guid
        )
        return (
          <PreviewElement
            key={`page-element-${element.id}`}
            element={element}
            entry={entry}
          />
        )
      })}
      {pageTableLayouts.map(tableLayout => (
        <PreviewTable
          key={`table-layout-${tableLayout.id}`}
          tableLayout={tableLayout}
          tableElements={tableScreenElements.filter(
            element => element.layout_id === tableLayout.id
          )}
        />
      ))}
    </Page>
  )
}

export const PDFPreview = () => {
  const pdfRef = useRef(null)
  const [loaded, setLoaded] = useState(false)
  const [previewContainerSize, setPreviewContainerSize] = useState({
    width: 0,
    height: 0,
  })

  const previewRef = useRef(null)
  const file = useSelector(pdfFileSelector)
  const pages = useSelector(pagesSelector)
  const activeSheet = useSelector(activeSheetSelector)
  const activePage = pages.find(page =>
    page.section_ids.includes(activeSheet?.section_id)
  )

  useEffect(() => {
    if (previewRef.current) {
      // TODO: This doesn't work as well as it should when things update.
      setPreviewContainerSize({
        width: previewRef.current.clientWidth,
        height: previewRef.current.clientHeight,
      })
    }
  }, [previewRef])

  return (
    <div className="pdf-preview--container" ref={previewRef}>
      {!loaded && <div className="spacer"></div>}
      <Document
        file={file}
        renderAnnotationLayer={false}
        onLoadSuccess={() => {
          setLoaded(true)
        }}
        className="pdf-document"
        inputRef={pdfRef}
      >
        <PreviewPage
          key={`pdf-page-${activePage?.page_number || 1}`}
          page={activePage}
          previewContainerSize={previewContainerSize}
        />
      </Document>
    </div>
  )
}
